import React, { useState, useEffect, useCallback } from 'react';
import {
  FiX,
  FiSearch,
  FiUploadCloud,
  FiImage,
  FiVideo,
  FiFile,
  FiFolder,
  FiGrid,
  FiList,
  FiCheck,
  FiTrash2,
  FiEdit2,
  FiExternalLink,
  FiCopy,
  FiMoreVertical,
  FiFilter,
  FiRefreshCw,
} from 'react-icons/fi';
import useCmsStore from '../../../services/useCmsStore';
import MediaUploader from './MediaUploader';
import './MediaLibrary.css';

const MEDIA_TYPES = [
  { value: '', label: 'All Types', icon: FiFile },
  { value: 'image', label: 'Images', icon: FiImage },
  { value: 'video', label: 'Videos', icon: FiVideo },
  { value: 'document', label: 'Documents', icon: FiFile },
];

const MediaLibrary = ({
  isOpen,
  onClose,
  onSelect,
  multiple = false,
  acceptedTypes = ['image', 'video', 'document'],
  title = 'Media Library',
}) => {
  const {
    media,
    mediaLoading,
    mediaPagination,
    fetchMedia,
    uploadMedia,
    updateMedia,
    deleteMedia,
  } = useCmsStore();

  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showUploader, setShowUploader] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, item: null });

  // Fetch media on mount and filter changes
  useEffect(() => {
    if (isOpen) {
      fetchMedia({
        search,
        media_type: selectedType,
        page: 1,
        per_page: 50,
      });
    }
  }, [isOpen, search, selectedType, fetchMedia]);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedItems([]);
    }
  }, [isOpen]);

  // Handle search with debounce
  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  // Handle item selection
  const handleItemClick = (item, e) => {
    if (e.ctrlKey || e.metaKey) {
      // Multi-select with ctrl/cmd
      setSelectedItems(prev =>
        prev.some(i => i.id === item.id)
          ? prev.filter(i => i.id !== item.id)
          : multiple ? [...prev, item] : [item]
      );
    } else if (e.shiftKey && selectedItems.length > 0 && multiple) {
      // Range select with shift
      const lastSelected = selectedItems[selectedItems.length - 1];
      const lastIndex = media.findIndex(m => m.id === lastSelected.id);
      const currentIndex = media.findIndex(m => m.id === item.id);
      const [start, end] = [Math.min(lastIndex, currentIndex), Math.max(lastIndex, currentIndex)];
      const rangeItems = media.slice(start, end + 1);
      setSelectedItems(rangeItems);
    } else {
      // Single select
      setSelectedItems([item]);
    }
  };

  // Handle double-click to select and close
  const handleItemDoubleClick = (item) => {
    if (onSelect) {
      onSelect(multiple ? [item] : item);
      onClose();
    }
  };

  // Handle insert button
  const handleInsert = () => {
    if (selectedItems.length > 0 && onSelect) {
      onSelect(multiple ? selectedItems : selectedItems[0]);
      onClose();
    }
  };

  // Handle context menu
  const handleContextMenu = (e, item) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      item,
    });
  };

  // Close context menu
  useEffect(() => {
    const handleClick = () => setContextMenu(prev => ({ ...prev, visible: false }));
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Handle delete
  const handleDelete = async (item) => {
    if (window.confirm(`Are you sure you want to delete "${item.filename}"?`)) {
      try {
        await deleteMedia(item.id);
        setSelectedItems(prev => prev.filter(i => i.id !== item.id));
      } catch (error) {
        console.error('Failed to delete media:', error);
      }
    }
  };

  // Handle copy URL
  const handleCopyUrl = (item) => {
    navigator.clipboard.writeText(item.url);
  };

  // Handle upload complete
  const handleUploadComplete = (uploadedMedia) => {
    setShowUploader(false);
    // Select newly uploaded items
    if (Array.isArray(uploadedMedia)) {
      setSelectedItems(uploadedMedia);
    } else {
      setSelectedItems([uploadedMedia]);
    }
  };

  // Handle edit save
  const handleEditSave = async (updatedData) => {
    try {
      await updateMedia(editingItem.id, updatedData);
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to update media:', error);
    }
  };

  // Get media icon
  const getMediaIcon = (type) => {
    switch (type) {
      case 'image':
        return FiImage;
      case 'video':
        return FiVideo;
      default:
        return FiFile;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="media-library-overlay" onClick={onClose}>
      <div className="media-library-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="media-library-header">
          <h2>{title}</h2>
          <button className="media-library-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="media-library-toolbar">
          <div className="media-library-toolbar-left">
            <button
              className="media-btn-upload"
              onClick={() => setShowUploader(true)}
            >
              <FiUploadCloud size={16} />
              <span>Upload</span>
            </button>
            <div className="media-search-wrapper">
              <FiSearch size={16} />
              <input
                type="text"
                placeholder="Search media..."
                value={search}
                onChange={handleSearch}
              />
            </div>
          </div>
          <div className="media-library-toolbar-right">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="media-type-select"
            >
              {MEDIA_TYPES.filter(t => !t.value || acceptedTypes.includes(t.value)).map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <div className="media-view-toggle">
              <button
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <FiGrid size={16} />
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <FiList size={16} />
              </button>
            </div>
            <button
              className="media-btn-refresh"
              onClick={() => fetchMedia({ search, media_type: selectedType })}
              title="Refresh"
            >
              <FiRefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="media-library-content">
          {showUploader ? (
            <MediaUploader
              onComplete={handleUploadComplete}
              onCancel={() => setShowUploader(false)}
              acceptedTypes={acceptedTypes}
            />
          ) : mediaLoading ? (
            <div className="media-library-loading">
              <div className="media-spinner" />
              <p>Loading media...</p>
            </div>
          ) : media.length === 0 ? (
            <div className="media-library-empty">
              <FiImage size={48} />
              <p>No media found</p>
              <button onClick={() => setShowUploader(true)}>
                <FiUploadCloud size={16} />
                <span>Upload your first file</span>
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="media-grid">
              {media.map(item => {
                const isSelected = selectedItems.some(s => s.id === item.id);
                const Icon = getMediaIcon(item.media_type);
                return (
                  <div
                    key={item.id}
                    className={`media-grid-item ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => handleItemClick(item, e)}
                    onDoubleClick={() => handleItemDoubleClick(item)}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                  >
                    <div className="media-grid-preview">
                      {item.media_type === 'image' ? (
                        <img src={item.thumbnail_url || item.url} alt={item.alt_text || item.filename} />
                      ) : item.media_type === 'video' ? (
                        <div className="media-grid-video">
                          {item.thumbnail_url ? (
                            <img src={item.thumbnail_url} alt={item.filename} />
                          ) : (
                            <FiVideo size={32} />
                          )}
                          <span className="media-video-badge">Video</span>
                        </div>
                      ) : (
                        <div className="media-grid-file">
                          <Icon size={32} />
                          <span className="media-file-ext">
                            {item.filename?.split('.').pop()?.toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="media-grid-info">
                      <span className="media-grid-name" title={item.filename}>
                        {item.filename}
                      </span>
                      <span className="media-grid-meta">
                        {formatFileSize(item.file_size)}
                      </span>
                    </div>
                    {isSelected && (
                      <div className="media-grid-check">
                        <FiCheck size={16} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="media-list">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Date</th>
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {media.map(item => {
                    const isSelected = selectedItems.some(s => s.id === item.id);
                    const Icon = getMediaIcon(item.media_type);
                    return (
                      <tr
                        key={item.id}
                        className={isSelected ? 'selected' : ''}
                        onClick={(e) => handleItemClick(item, e)}
                        onDoubleClick={() => handleItemDoubleClick(item)}
                      >
                        <td>
                          <div className="media-list-checkbox">
                            {isSelected && <FiCheck size={14} />}
                          </div>
                        </td>
                        <td>
                          <div className="media-list-name">
                            {item.media_type === 'image' ? (
                              <img
                                src={item.thumbnail_url || item.url}
                                alt=""
                                className="media-list-thumb"
                              />
                            ) : (
                              <div className="media-list-icon">
                                <Icon size={20} />
                              </div>
                            )}
                            <span>{item.filename}</span>
                          </div>
                        </td>
                        <td>{item.mime_type || item.media_type}</td>
                        <td>{formatFileSize(item.file_size)}</td>
                        <td>{formatDate(item.created_at)}</td>
                        <td>
                          <div className="media-list-actions">
                            <button
                              title="Edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingItem(item);
                              }}
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button
                              title="Copy URL"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyUrl(item);
                              }}
                            >
                              <FiCopy size={14} />
                            </button>
                            <button
                              title="Delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item);
                              }}
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="media-library-footer">
          <div className="media-library-selection">
            {selectedItems.length > 0 && (
              <span>{selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected</span>
            )}
          </div>
          <div className="media-library-actions">
            <button className="media-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="media-btn-insert"
              disabled={selectedItems.length === 0}
              onClick={handleInsert}
            >
              {multiple ? 'Insert Selected' : 'Insert'}
            </button>
          </div>
        </div>

        {/* Context Menu */}
        {contextMenu.visible && (
          <div
            className="media-context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <button onClick={() => setEditingItem(contextMenu.item)}>
              <FiEdit2 size={14} />
              <span>Edit details</span>
            </button>
            <button onClick={() => handleCopyUrl(contextMenu.item)}>
              <FiCopy size={14} />
              <span>Copy URL</span>
            </button>
            <button onClick={() => window.open(contextMenu.item.url, '_blank')}>
              <FiExternalLink size={14} />
              <span>Open in new tab</span>
            </button>
            <hr />
            <button className="danger" onClick={() => handleDelete(contextMenu.item)}>
              <FiTrash2 size={14} />
              <span>Delete</span>
            </button>
          </div>
        )}

        {/* Edit Modal */}
        {editingItem && (
          <MediaDetailsModal
            item={editingItem}
            onSave={handleEditSave}
            onClose={() => setEditingItem(null)}
          />
        )}
      </div>
    </div>
  );
};

// Media Details Edit Modal
const MediaDetailsModal = ({ item, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: item.title || '',
    alt_text: item.alt_text || '',
    caption: item.caption || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="media-details-overlay" onClick={onClose}>
      <div className="media-details-modal" onClick={e => e.stopPropagation()}>
        <div className="media-details-header">
          <h3>Edit Media Details</h3>
          <button onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>
        <div className="media-details-content">
          <div className="media-details-preview">
            {item.media_type === 'image' ? (
              <img src={item.url} alt={item.alt_text || item.filename} />
            ) : item.media_type === 'video' ? (
              <video src={item.url} controls />
            ) : (
              <div className="media-details-file">
                <FiFile size={48} />
                <span>{item.filename}</span>
              </div>
            )}
          </div>
          <form onSubmit={handleSubmit}>
            <div className="media-details-form">
              <div className="form-group">
                <label>Filename</label>
                <input type="text" value={item.filename} disabled />
              </div>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Media title"
                />
              </div>
              <div className="form-group">
                <label>Alt Text</label>
                <input
                  type="text"
                  value={formData.alt_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, alt_text: e.target.value }))}
                  placeholder="Alternative text for accessibility"
                />
                <span className="form-hint">Describe the image for screen readers and SEO</span>
              </div>
              <div className="form-group">
                <label>Caption</label>
                <textarea
                  value={formData.caption}
                  onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                  placeholder="Caption or description"
                  rows={3}
                />
              </div>
              <div className="media-details-info">
                <div className="info-row">
                  <span>URL:</span>
                  <code>{item.url}</code>
                </div>
                <div className="info-row">
                  <span>Size:</span>
                  <span>{(item.file_size / 1024).toFixed(1)} KB</span>
                </div>
                {item.width && item.height && (
                  <div className="info-row">
                    <span>Dimensions:</span>
                    <span>{item.width} x {item.height}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="media-details-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-save">
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MediaLibrary;
